import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { sortAdvertisementsByDate } from "../../../utils/advertisementSort";
import { adsWithBannerImages } from "../../../utils/advertisementBannerSpec";

const BANNER_H = { xs: 180, sm: 240, md: 320 };

const CompetitionAdvertisementCarousel = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchAds = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL_USER}/getEnabledAdvertisements`,
          { headers }
        );
        if (!cancelled && response.data?.success) {
          const rows = Array.isArray(response.data.data)
            ? response.data.data
            : [];
          setAds(adsWithBannerImages(sortAdvertisementsByDate(rows)));
        }
      } catch {
        if (!cancelled) setAds([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchAds();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!loaded || ads.length === 0) {
    return null;
  }

  const canLoop = ads.length > 1;

  return (
    <Box
      className="competition-ad-carousel"
      sx={{
        mb: 3,
        mx: { xs: -1, md: 0 },
        width: "100%",
        "& .swiper": { width: "100%", overflow: "hidden" },
        "& .swiper-slide": {
          height: BANNER_H,
          boxSizing: "border-box",
        },
        "& .swiper-wrapper": {
          alignItems: "stretch",
        },
        "& .swiper-pagination": {
          position: "relative",
          mt: 1.5,
        },
        "& .swiper-pagination-bullet": {
          bgcolor: "rgba(255,255,255,0.45)",
          opacity: 1,
        },
        "& .swiper-pagination-bullet-active": {
          bgcolor: "#ef364e",
        },
      }}
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        spaceBetween={12}
        slidesPerView={1}
        loop={canLoop}
        autoplay={
          canLoop ? { delay: 4500, disableOnInteraction: false } : false
        }
        pagination={{ clickable: true }}
      >
        {ads.map((ad) => {
          const image = (
            <Box
              component="img"
              src={ad.banner_image}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center",
                display: "block",
              }}
            />
          );

          const frameSx = {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            borderRadius: 1.5,
            bgcolor: "#242743",
          };

          return (
            <SwiperSlide key={ad.id}>
              {ad.link ? (
                <Box
                  component="a"
                  href={ad.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Advertisement"
                  sx={frameSx}
                >
                  {image}
                </Box>
              ) : (
                <Box sx={frameSx} aria-label="Advertisement">
                  {image}
                </Box>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
};

export default CompetitionAdvertisementCarousel;
